import React, { useMemo, useState } from "react";
import { usePageView } from "../../utils/track";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Download,
  HelpCircle,
  LineChart,
  Medal,
  Layers,
  Search as SearchIcon,
  ShieldCheck,
  Trophy,
  Users,
  UsersRound,
} from "lucide-react";
import ClassRankingBoards, { buildBoards, ClassLeaders } from "../../components/ClassRankingBoards";
import { Avatar, Pagination, RankChip, Select, StatCard } from "../../components/ui";
import { useApi } from "../../api/client";
import { RankingRow } from "../../data/types";
import { downloadCsv } from "../../utils/download";
import { formatPercent } from "../../utils/score";

// Ranking boards per the proposal's core results: Overall, Class, Grade, Category, Team
const rankNav = [
  { to: "/rankings", label: "All Classes", icon: LineChart, end: true },
  { to: "/rankings/class", label: "Class Rankings", icon: ShieldCheck },
  // Grade boards live inside the class page — a grade divides a class, so it
  // cannot be ranked without one. /rankings/grade had no page behind it and
  // silently bounced back here.
  { to: "/rankings/class?split=grade", label: "Grade Rankings", icon: Medal },
  { to: "/rankings/category", label: "Category Rankings", icon: Layers },
  { to: "/rankings/teams", label: "Teams / Clubs Rankings", icon: UsersRound },
];

export function RankingsSidebar({ title, subtitle }: { title: string; subtitle: string }) {
  // Grade Rankings is the class page with the split turned on.
  const splitOn = useLocation().search.includes("split=grade");
  /*
   * The qualifying minimum, read from the portal rather than written in here.
   * An Association that raises it to 150 should not find the page still saying
   * 100; the setting is the rule, and this only reports it.
   */
  const { data: settings } = useApi<{ minRankedShots?: number }>("/settings", {});
  const minShots = settings.minRankedShots ?? 100;
  return (
    <aside className="w-full shrink-0 space-y-6 lg:w-[268px]">
      <div className="card p-4">
        <div className="flex items-start gap-3">
          <Trophy size={30} className="text-warning" />
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-ink">{title}</h3>
            <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p>
            <p className="mt-2 text-xs font-semibold text-slate-500">As of {new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p>
          </div>
        </div>
      </div>
      <div className="card p-3">
        <nav className="space-y-0.5">
          {rankNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={label}
              to={to}
              end={end}
              className={({ isActive }) => {
                /*
                 * Class and Grade are one page told apart by ?split=grade, and
                 * NavLink ignores the query — so on /rankings/class it lit both.
                 * Whichever of the two matches the current query is the active
                 * one; every other item keeps NavLink's own answer.
                 */
                const wantsSplit = to.includes("split=grade");
                const onClassPage = to.split("?")[0] === "/rankings/class";
                const active = onClassPage ? isActive && wantsSplit === splitOn : isActive;
                return `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? "border-l-[3px] border-primary bg-primary-soft text-primary"
                    : "text-slate-600 hover:bg-slate-50 hover:text-ink"
                }`;
              }
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
      {/*
        Who qualifies, stated on the page.

        The rule was real but invisible: a class with nobody past the minimum
        simply did not appear, and a club whose shooters were missing had no way
        to tell whether that was the rule working or the portal failing.
      */}
      <div className="card bg-slate-50 p-4">
        <h4 className="text-xs font-extrabold uppercase tracking-widest text-ink">About Rankings</h4>
        <p className="mt-2.5 text-[13px] leading-relaxed text-muted">
          Every board is ranked within one rifle class, on average score and then best score. Classes score on different
          scales, so they are never placed on a single ladder.
        </p>
        <h5 className="mt-3.5 border-t border-line pt-3 text-xs font-extrabold uppercase tracking-widest text-ink">
          Who Qualifies
        </h5>
        <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-muted">
          <li>
            A competitor appears once they have shot{" "}
            <b className="text-ink">{minShots.toLocaleString()} record shots</b> in that class. Sighters do not count.
          </li>
          <li>Each class gets its own board as soon as one of its competitors reaches that — nothing is set up by hand.</li>
          <li>Boards open at the top 10; the class expands to every qualifier.</li>
        </ul>
      </div>
    </aside>
  );
}

export function RankingsTable({ rows }: { rows?: RankingRow[] }) {
  const navigate = useNavigate();
  const { data: fetched } = useApi<RankingRow[]>("/rankings", []);
  const rankings = rows ?? fetched;
  // Every shooter on one page; the card scrolls rather than the list splitting.
  const paged = rankings;

  return (
    <div className="card mt-4">
      <div className="table-scroll [--table-offset:400px]">
        <table className="w-full min-w-[860px]">
        <thead>
          <tr className="border-b border-line bg-slate-50">
            <th className="th">Rank</th>
            <th className="th">Shooter</th>
            <th className="th">SID</th>
            <th className="th">Class</th>
            <th className="th">Grade</th>
            <th className="th text-right">Events</th>
            <th className="th text-right">Overall %</th>
            <th className="th text-right">Personal Best</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {paged.map((r, i) => (
            <tr key={r.sid} onClick={() => navigate(`/shooters/${r.sid}`)} className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
              <td className="td"><RankChip rank={r.rank} /></td>
              <td className="td">
                <div className="flex items-center gap-3">
                  <Avatar name={r.shooter} />
                  <div>
                    <Link to={`/shooters/${r.sid}`} className="font-bold text-ink hover:text-primary">{r.shooter}</Link>
                    <div className="text-xs text-muted">{r.club}</div>
                  </div>
                </div>
              </td>
              <td className="td text-muted">{r.sid}</td>
              <td className="td">{r.rifleClass}</td>
              <td className="td">{r.grade}</td>
              <td className="td text-right">{r.competitions}</td>
              <td className="td text-right font-bold">{formatPercent(r.avgPercent)}</td>
              <td className="td text-right font-bold text-primary">{formatPercent(r.bestPercent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {rankings.length === 0 && <div className="p-10 text-center text-sm text-muted">No shooters match the current filters.</div>}
      <div className="border-t border-line">
        <Pagination summary={`${rankings.length} ${rankings.length === 1 ? "shooter" : "shooters"}`} />
      </div>
    </div>
  );
}

export function TopPerformerCard() {
  const { data: rankings } = useApi<RankingRow[]>("/rankings", []);
  const top = rankings[0];
  if (!top) {
    return (
      <div className="card border-line p-4">
        <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink"><Trophy size={16} className="text-muted" /> Top Performer</h4>
        <p className="mt-3 text-[13px] leading-snug text-muted">Rankings appear once a shoot has been imported.</p>
      </div>
    );
  }
  return (
    <div className="card border-success/30 bg-success-soft/60 p-4">
      <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink"><Trophy size={16} className="text-success" /> Top Performer</h4>
      <div className="mt-4 flex items-center gap-3.5">
        <Avatar name={top.shooter} size={52} />
        <div>
          <div className="text-[15px] font-bold text-ink">{top.shooter}</div>
          <div className="text-[13px] text-muted">{top.club}</div>
          <span className="mt-1.5 inline-block rounded-md bg-success px-2 py-0.5 text-[10px] font-extrabold text-white">RANK #1</span>
        </div>
      </div>
      <div className="mt-4 space-y-2 border-t border-success/20 pt-4 text-sm">
        {[
          ["Overall %", formatPercent(top.avgPercent)],
          ["Personal Best", formatPercent(top.bestPercent)],
          ["Events", String(top.competitions)],
        ].map(([l, v]) => (
          <div key={l} className="flex justify-between">
            <span className="text-muted">{l}</span>
            <span className="font-bold text-ink">{v}</span>
          </div>
        ))}
      </div>
      <Link to={`/shooters/${top.sid}`} className="mt-4 block text-sm font-bold text-primary hover:underline">
        View Shooter Profile →
      </Link>
    </div>
  );
}

export default function OverallRankings() {
  usePageView("rankings");
  const { data: allRankings } = useApi<RankingRow[]>("/rankings", []);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("All Classes");
  const [gradeFilter, setGradeFilter] = useState("All Grades");
  const [stateFilter, setStateFilter] = useState("All States");

  /*
   * Filter options come from the ranked shooters themselves. A fixed list named
   * classes and states this portal may never see, and hid the ones it does —
   * an imported shoot fields whatever the entry sheet declared.
   */
  const classOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.rifleClass).filter(Boolean))).sort(),
    [allRankings]
  );
  const gradeOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.grade).filter(Boolean))).sort(),
    [allRankings]
  );
  const stateOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.stateCode).filter(Boolean))).sort(),
    [allRankings]
  );

  const q = search.trim().toLowerCase();
  const filtered = allRankings.filter(
    (r) =>
      (!q || r.shooter.toLowerCase().includes(q) || r.sid.includes(q)) &&
      (classFilter === "All Classes" || r.rifleClass === classFilter) &&
      (gradeFilter === "All Grades" || r.grade === gradeFilter) &&
      (stateFilter === "All States" || r.stateCode === stateFilter)
  );

  // Exported class by class, with the rank being the placing within the class —
  // which is what the page now shows and what a competitor is awarded.
  const exportCsv = () =>
    downloadCsv(
      "rankings-by-class.csv",
      ["Class", "Class Rank", "Shooter", "SID", "Club", "State", "Grade", "Events", "Overall %", "Personal Best %"],
      buildBoards(filtered).flatMap((b) =>
        b.rows.map((r) => [
          b.rifleClass,
          r.rank,
          r.shooter,
          r.sid,
          r.club,
          r.stateCode,
          r.grade,
          r.competitions,
          formatPercent(r.avgPercent),
          formatPercent(r.bestPercent),
        ])
      )
    );

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <div className="flex flex-col gap-4 lg:flex-row">
        <RankingsSidebar title="Rankings" subtitle="Ranked within class · All competitions" />

        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Rankings</h1>
          <p className="mt-1 text-[13px] text-muted">
            Ranked on <b className="text-ink">percentage of the possible</b>, within their own class. Target Rifle, F-TR,
            F-Open and Sporter/Hunter are <b className="text-ink">separate disciplines</b>, not grades of one — different faces,
            different rules, 5 points a shot against 6 — so each holds its own board and its own winner, and a competitor
            entered in two of them is ranked in both, on the results they shot in each.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard icon={<Users size={20} />} value={allRankings.length.toLocaleString()} label="Shooters Ranked" />
            <StatCard icon={<ShieldCheck size={20} />} value={classOptions.length} label="Classes" sub="Ranked separately" iconBg="bg-success-soft text-success" />
            <StatCard icon={<Medal size={20} />} value={allRankings.filter((r) => r.avgScore > 0).length} label="With Scored Results" iconBg="bg-violet-50 text-violet-600" />
            <StatCard icon={<Trophy size={20} />} value={Math.max(0, ...allRankings.map((r) => r.competitions))} label="Most Events Counted" iconBg="bg-warning-soft text-warning" />
          </div>

          <div className="card mt-4 flex flex-wrap items-center gap-3 p-4">
            <div className="relative w-64 flex-1">
              <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="input pl-9" placeholder="Search shooter by name or SID..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select options={["All Classes", ...classOptions]} value={classFilter} onChange={setClassFilter} className="w-full sm:w-36" />
            <Select options={["All Grades", ...gradeOptions]} value={gradeFilter} onChange={setGradeFilter} className="w-full sm:w-32" />
            <Select options={["All States", ...stateOptions]} value={stateFilter} onChange={setStateFilter} className="w-full sm:w-32" />
            <button
              onClick={exportCsv}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>

          {/* One ranked board per class — see ClassRankingBoards for why. */}
          <div className="mt-4">
            <ClassRankingBoards rows={filtered} emptyMessage="No shooters match the current filters." />
          </div>
        </div>

        {/*
          The filter panel that used to sit here duplicated the filters above the
          table, except its options were a fixed list and its "Apply" button did
          nothing. The working filters are the ones over the results.
        */}
        <aside className="w-full shrink-0 space-y-4 lg:w-[268px]">
          <ClassLeaders rows={allRankings} />
          <div className="card p-4">
            <h4 className="text-sm font-extrabold text-ink">Ranking Information</h4>
            <ul className="mt-2 space-y-2 text-[13px] leading-snug text-muted">
              <li className="flex gap-2"><HelpCircle size={14} className="mt-0.5 shrink-0" /> Rankings are recalculated after every import.</li>
              <li className="flex gap-2"><HelpCircle size={14} className="mt-0.5 shrink-0" /> Ranked on average score, then best score.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
